// src/lib/progress/metric-labels.ts
//
// ONE name and ONE plain-language definition for each per-topic progress
// metric a student sees. QA: "Different screens use lifetime accuracy, newer
// performance estimates, and mastery tiers" — the numbers were honest, but a
// bare "72% accuracy" on one card and "64%" on the next, for the same topic,
// read as a bug. Each surface now says WHICH number it is, using these labels,
// and where two different ones sit near each other it says why they differ.
//
// This module only NAMES metrics. It never computes one:
//   - All-time accuracy  → getTopicMastery (src/lib/scoring/topic-mastery.ts)
//   - Recent accuracy    → queryTopicPerformance / getWeakTopics
//                          (src/lib/scoring/topic-performance.ts), decayed with
//                          HALF_LIFE_DAYS (src/lib/scoring/accuracy-decay.ts)
//   - Mastery            → masteryTier (src/lib/test-engine/sat-domains.ts)
//   - Score estimate     → computeCurrentScore / loadCurrentScore
//
// A source scan (src/lib/progress/__tests__/metric-labels.test.ts) keeps the
// listed surfaces from going back to hand-typed "accuracy" labels.

import { HALF_LIFE_DAYS } from '@/lib/scoring/accuracy-decay'
import {
  MASTERY_DISPLAY,
  MASTERY_MIN_ATTEMPTS,
  MASTERY_THRESHOLDS,
  type MasteryTier,
} from '@/lib/test-engine/sat-domains'

export type MetricKey = 'allTime' | 'recent' | 'mastery'

export type MetricLabel = {
  /** Title-case name, for headings and legends ("All-time accuracy"). */
  name: string
  /** Lower-case form for use mid-sentence ("72% all-time accuracy"). */
  inline: string
  /** A few words, for a legend that has no room for the full sentence. */
  hint: string
  /** One sentence: what the number is. */
  explain: string
}

const { gold, silver, bronze } = MASTERY_THRESHOLDS

export const METRIC_LABELS: Record<MetricKey, MetricLabel> = {
  allTime: {
    name: 'All-time accuracy',
    inline: 'all-time accuracy',
    hint: 'every question you have answered',
    explain:
      'Share of every question you have answered (tests, practice and drills) that you got right, each question counted once at your latest answer.',
  },
  recent: {
    name: 'Recent accuracy',
    inline: 'recent accuracy',
    hint: 'weighted toward your last few weeks',
    explain: `Your accuracy weighted toward recent work: an answer from ${HALF_LIFE_DAYS} days ago counts half as much as one from today.`,
  },
  mastery: {
    name: 'Mastery',
    inline: 'mastery',
    hint: `a tier once a topic has ${MASTERY_MIN_ATTEMPTS}+ answers`,
    explain: `A tier for a topic once you have ${MASTERY_MIN_ATTEMPTS}+ answers in it: Gold ${gold}%+, Silver ${silver}%+, Bronze ${bronze}%+, In progress under ${bronze}%.`,
  },
}

/**
 * The note shown wherever Recent and All-time accuracy can both be seen for
 * the same topic (the Practice console links to the all-topics list; the
 * dashboard's Leverage Board sits above the Mastery Map).
 */
export const WHY_RECENT_DIFFERS =
  'Recent accuracy weights your last few weeks more, so it moves faster than all-time accuracy: after a strong week it runs ahead, after a break it drifts back.'

/** The rule behind one mastery tier, for a legend chip's tooltip. */
export function masteryTierRule(tier: MasteryTier): string {
  const label = MASTERY_DISPLAY[tier].label
  switch (tier) {
    case 'gold':
      return `${label}: ${gold}%+ with ${MASTERY_MIN_ATTEMPTS}+ answers`
    case 'silver':
      return `${label}: ${silver}%+ with ${MASTERY_MIN_ATTEMPTS}+ answers`
    case 'bronze':
      return `${label}: ${bronze}%+ with ${MASTERY_MIN_ATTEMPTS}+ answers`
    case 'in_progress':
      return `${label}: under ${bronze}% with ${MASTERY_MIN_ATTEMPTS}+ answers`
    case 'untouched':
      return `${label}: fewer than ${MASTERY_MIN_ATTEMPTS} answers, not enough to rate yet`
  }
}
